import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "../context/AuthContext";
import { Navbar } from "../components/Navbar";
import { Seo } from "../components/Seo";
import { Footer } from "../components/Footer";
import { Screenshot } from "../components/Screenshot";
import { HOME } from "../data/homepage";
import { Users, Route, BadgeCheck, ArrowRight } from "lucide-react";

const ICONS = { collab: Users, route: Route, verified: BadgeCheck };

export default function Landing() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const go = () => navigate(user ? "/plan" : "/auth");

  return (
    <div className="relative min-h-screen grain" data-testid="landing-page">
      <Seo
        title=""
        description={HOME.description}
        path="/"
        jsonLd={{
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "Organization",
              "@id": "https://wanderlyme.com/#organization",
              name: "WanderlyMe",
              url: "https://wanderlyme.com/",
              logo: "https://wanderlyme.com/og-default.jpg",
              email: "hello@wanderlyme.com",
              founder: { "@type": "Person", name: "Gurman Soni" },
              areaServed: { "@type": "Country", name: "Australia" },
              address: { "@type": "PostalAddress", addressLocality: "Sydney", addressCountry: "AU" },
            },
            { "@type": "WebSite", name: "WanderlyMe", url: "https://wanderlyme.com/", description: HOME.description, publisher: { "@id": "https://wanderlyme.com/#organization" } },
          ],
        }}
      />
      <Navbar />

      <section className="max-w-6xl mx-auto px-6 pt-16 pb-20 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="lg:col-span-5">
          <span className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-[#C8553D] mb-6" data-testid="hero-eyebrow">
            <BadgeCheck className="w-4 h-4" /> {HOME.eyebrow}
          </span>
          <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tight">
            {HOME.h1a}<em className="text-[#C8553D]">{HOME.h1em}</em>{HOME.h1b}
          </h1>
          <p className="mt-6 text-lg text-muted-foreground max-w-md leading-relaxed" data-testid="hero-subheadline">{HOME.sub}</p>
          <div className="mt-9 flex flex-wrap items-center gap-4">
            <button onClick={go} data-testid="hero-start-btn" className="group px-7 py-3.5 rounded-full bg-[#1A3B2B] text-[#FDFBF7] hover:bg-[#122a1f] transition-colors flex items-center gap-2">
              {HOME.cta}
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </button>
            <Link to="/features" data-testid="hero-features-link" className="text-sm underline underline-offset-4 hover:text-[#C8553D] transition-colors">
              {HOME.ctaSecondary}
            </Link>
          </div>
        </motion.div>

        <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.7, delay: 0.1 }} className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-5 gap-4">
          <div className="sm:col-span-3 h-72 sm:h-[26rem]"><Screenshot id="mapRoute" testId="hero-screenshot-map" /></div>
          <div className="sm:col-span-2 h-72 sm:h-[26rem]"><Screenshot id="collab" testId="hero-screenshot-collab" /></div>
        </motion.div>
      </section>

      <section className="max-w-6xl mx-auto px-6 pb-10">
        <ul className="flex flex-wrap gap-x-8 gap-y-2 text-sm text-muted-foreground" data-testid="hero-proof">
          {HOME.proof.map(([k, v]) => (
            <li key={k}><span className="font-semibold text-[#1A3B2B]">{k}</span> {v}</li>
          ))}
        </ul>
      </section>

      <section className="max-w-6xl mx-auto px-6 pb-28 grid grid-cols-1 md:grid-cols-3 gap-6" data-testid="feature-cards">
        {HOME.features.map((f, i) => {
          const Icon = ICONS[f.key];
          return (
            <motion.div key={f.key} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: i * 0.1 }} className="p-8 rounded-2xl border border-black/5 bg-white/60 hover:-translate-y-1 transition-transform" data-testid={`feature-card-${f.key}`}>
              <Icon className="w-7 h-7 text-[#C8553D] mb-5" strokeWidth={1.5} />
              <h2 className="font-display text-2xl mb-2">{f.title}</h2>
              <p className="text-muted-foreground leading-relaxed">{f.body}</p>
            </motion.div>
          );
        })}
      </section>

      <section className="max-w-6xl mx-auto px-6 pb-28">
        <div className="rounded-3xl bg-[#1A3B2B] text-[#FDFBF7] p-8 sm:p-12 grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
          <div>
            <p className="text-xs uppercase tracking-widest text-[#C8553D] mb-3">Prefer a human touch?</p>
            <h2 className="font-display text-3xl sm:text-4xl leading-tight mb-3">Have a person check every line before you book.</h2>
            <p className="opacity-85 leading-relaxed mb-6 max-w-lg">Human-verified plans delivered within 48 hours — every hour, price and booking re-checked by hand.</p>
            <Link to="/plan-with-us" data-testid="home-plan-with-us-cta" className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#C8553D] text-[#FDFBF7] hover:bg-[#a8412f] transition-colors text-sm">
              Plan with us <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="h-56 sm:h-64"><Screenshot id="verified" testId="home-screenshot-verified" /></div>
        </div>
      </section>
      <Footer />
    </div>
  );
}
